import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateMetaTags, addStructuredData } from '../utils/seo';
import { FaLinkedin } from 'react-icons/fa';

const BusinessNetworkingBasics: React.FC = () => {
  useEffect(() => {
    updateMetaTags({
      title: 'Business Networking Basics Guide | Explanatum',
      description: 'Master the fundamentals of professional networking to expand your business opportunities and build meaningful connections.',
      ogTitle: 'Business Networking Basics: The Complete Guide',
      ogDescription: 'Learn effective networking strategies, conversation starters, follow-up techniques, and how to build lasting professional relationships.',
      canonicalUrl: 'https://www.explanatum.co.uk/business-networking-basics'
    });
    
    addStructuredData({
      '@context': 'https://schema.org',
      '@type': 'Article',
      'headline': 'Business Networking Basics: The Complete Guide',
      'description': 'Master the fundamentals of professional networking to expand your business opportunities and build meaningful connections.',
      'image': 'https://www.explanatum.co.uk/images/networking-guide-cover.jpg',
      'author': {
        '@type': 'Organization',
        'name': 'Explanatum',
        'url': 'https://www.explanatum.co.uk'
      },
      'publisher': {
        '@type': 'Organization',
        'name': 'Explanatum',
        'logo': {
          '@type': 'ImageObject',
          'url': 'https://www.explanatum.co.uk/logo.png'
        }
      },
      'datePublished': '2025-08-01',
      'dateModified': '2025-08-01'
    }, 'business-networking-basics-structured-data');
  }, []);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-teal-50 z-0"></div>
        <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIiBmaWxsPSJub25lIj48cGF0aCBkPSJNLjUuNWg2MHY2MEguNVYuNXptMzAgMTVhMTUgMTUgMCAwIDAtMTUgMTUgMTUgMTUgMCAwIDAgMTUgMTUgMTUgMTUgMCAwIDAgMTUtMTUgMTUgMTUgMCAwIDAtMTUtMTV6bTAgMGExNSAxNSAwIDAgMC0xNSAxNSAxNSAxNSAwIDAgMCAxNSAxNSAxNSAxNSAwIDAgMCAxNS0xNSAxNSAxNSAwIDAgMC0xNS0xNXoiIHN0cm9rZT0iIzIwMjAyMCIgc3Ryb2tlLW9wYWNpdHk9Ii4wNiIvPjwvc3ZnPg==')]"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-blue-600">
              Business Networking Basics
            </h1>
            <p className="text-xl text-gray-700 mb-8">
              Master the fundamentals of professional networking to expand your business opportunities and build meaningful connections.
            </p>
            <div className="flex justify-center gap-4">
              <a href="#download-guide" className="px-8 py-3 bg-gradient-to-r from-teal-500 to-blue-500 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
                Download Free Guide
              </a>
              <Link to="/free-training-materials" className="px-8 py-3 bg-white text-blue-600 border border-blue-200 font-medium rounded-lg shadow hover:shadow-md transition-all">
                View All Resources
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">In This Guide</h2>
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm border border-gray-100">
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center font-medium text-sm">1</span>
                  <a href="#why-networking" className="text-blue-600 hover:underline font-medium">Why Networking Matters for Business Success</a>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center font-medium text-sm">2</span>
                  <a href="#preparing" className="text-blue-600 hover:underline font-medium">Preparing for Networking Events</a>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center font-medium text-sm">3</span>
                  <a href="#conversation" className="text-blue-600 hover:underline font-medium">Conversation Starters and Engagement Techniques</a>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center font-medium text-sm">4</span>
                  <a href="#follow-up" className="text-blue-600 hover:underline font-medium">Effective Follow-up Strategies</a>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center font-medium text-sm">5</span>
                  <a href="#tracking" className="text-blue-600 hover:underline font-medium">Tracking and Nurturing Professional Relationships</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Sections */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Section 1 */}
            <div id="why-networking" className="mb-16">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center">
                  <span className="text-2xl font-bold">🤝</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-800">Why Networking Matters for Business Success</h2>
              </div>
              <div className="prose prose-lg max-w-none">
                <p>
                  Effective business networking is more than collecting business cards—it's about building mutually beneficial relationships that can propel your career and business forward. Research shows that up to 85% of jobs are filled through networking, and businesses with strong professional networks are 74% more likely to succeed in their first five years.
                </p>
                <h3>Key Benefits of Strategic Networking</h3>
                <ul>
                  <li><strong>Access to Opportunities:</strong> Networking opens doors to new business partnerships, client relationships, and career advancements that might otherwise remain hidden.</li>
                  <li><strong>Knowledge Exchange:</strong> Regular interaction with other professionals provides insights into industry trends, best practices, and innovative solutions.</li>
                  <li><strong>Increased Visibility:</strong> Active networking raises your professional profile and positions you as a thought leader in your field.</li>
                  <li><strong>Support System:</strong> A strong network provides mentorship, advice, and emotional support during challenging business periods.</li>
                </ul>
                <blockquote>
                  "Your network is your net worth. The relationships you form throughout your career can be your most valuable business asset."
                </blockquote>
              </div>
            </div>

            {/* Section 2 */}
            <div id="preparing" className="mb-16">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                  <span className="text-2xl font-bold">👥</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-800">Preparing for Networking Events</h2>
              </div>
              <div className="prose prose-lg max-w-none">
                <p>
                  Successful networking begins long before you enter the room. Proper preparation ensures you make the most of every networking opportunity, whether it's a formal conference or a casual industry meetup.
                </p>
                <h3>Pre-Event Preparation Checklist</h3>
                <ul>
                  <li><strong>Research the Event:</strong> Understand the event's purpose, attendee list, and agenda to identify key opportunities.</li>
                  <li><strong>Define Your Goals:</strong> Set specific objectives for each event (e.g., meet three potential clients, find a mentor in your industry).</li>
                  <li><strong>Craft Your Elevator Pitch:</strong> Develop a concise, compelling introduction that clearly communicates who you are and the value you provide.</li>
                  <li><strong>Prepare Thoughtful Questions:</strong> Create a list of open-ended questions that demonstrate your interest in others and spark meaningful conversations.</li>
                </ul>
                <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 my-6">
                  <h4 className="text-blue-700 font-semibold mb-3">Perfect Your Elevator Pitch Template</h4>
                  <p className="text-gray-700 mb-4">Use this formula to create a memorable introduction:</p>
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>I'm [your name] from [your company/organization].</li>
                    <li>I help [target audience] to [solve this problem/achieve this outcome].</li>
                    <li>What makes us unique is [your unique value proposition].</li>
                    <li>For example, [brief success story or example].</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Section 3 */}
            <div id="conversation" className="mb-16">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
                  <span className="text-2xl font-bold">💬</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-800">Conversation Starters and Engagement Techniques</h2>
              </div>
              <div className="prose prose-lg max-w-none">
                <p>
                  The art of conversation is at the heart of effective networking. Knowing how to initiate and sustain meaningful discussions can transform brief encounters into valuable connections.
                </p>
                <h3>Proven Conversation Starters</h3>
                <ul>
                  <li>"What brings you to this event?"</li>
                  <li>"What's been the most interesting part of the conference for you so far?"</li>
                  <li>"I noticed you work in [industry]. What current trends are you most excited about?"</li>
                  <li>"What projects are you currently working on that you're passionate about?"</li>
                </ul>
                <h3>Active Listening Techniques</h3>
                <ul>
                  <li><strong>Maintain Eye Contact:</strong> Show that you're fully engaged in the conversation.</li>
                  <li><strong>Ask Follow-up Questions:</strong> Demonstrate genuine interest by digging deeper into topics they mention.</li>
                  <li><strong>Paraphrase and Reflect:</strong> "So what you're saying is..." confirms understanding and shows attentiveness.</li>
                  <li><strong>Avoid Interrupting:</strong> Allow others to complete their thoughts before responding.</li>
                </ul>
                <div className="bg-purple-50 p-6 rounded-lg border border-purple-100 my-6">
                  <h4 className="text-purple-700 font-semibold mb-3">The 70/30 Rule</h4>
                  <p>Aim to listen 70% of the time and speak 30% of the time. This balance ensures you gather valuable information while still sharing enough about yourself to form a connection.</p>
                </div>
              </div>
            </div>

            {/* More sections would continue here... */}
            
            {/* Section 4 */}
            <div id="follow-up" className="mb-16">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center">
                  <span className="text-2xl font-bold">📅</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-800">Effective Follow-up Strategies</h2>
              </div>
              <div className="prose prose-lg max-w-none">
                <p>
                  The real value of networking emerges after the initial meeting. Thoughtful follow-up transforms a brief encounter into a lasting professional relationship.
                </p>
                <h3>Follow-up Best Practices</h3>
                <ul>
                  <li><strong>Timing is Critical:</strong> Send a follow-up message within 24-48 hours while the conversation is still fresh.</li>
                  <li><strong>Personalize Your Message:</strong> Reference specific topics discussed to demonstrate attentiveness.</li>
                  <li><strong>Provide Value:</strong> Share an article, resource, or introduction related to your conversation.</li>
                  <li><strong>Suggest Next Steps:</strong> Propose a concrete action like a coffee meeting, video call, or collaboration opportunity.</li>
                </ul>
                <div className="bg-amber-50 p-6 rounded-lg border border-amber-100 my-6">
                  <h4 className="text-amber-700 font-semibold mb-3">Follow-up Email Template</h4>
                  <pre className="bg-white p-4 rounded text-sm">
{`Subject: Great Meeting You at [Event Name]

Hi [Name],

It was a pleasure meeting you at [event name] yesterday. I particularly enjoyed our conversation about [specific topic discussed].

As promised, here's the [article/resource/link] on [relevant topic] that I mentioned. I thought you might find it valuable for your work on [their project/interest].

I'd love to continue our conversation. Would you be available for a quick coffee or virtual meeting next week? I'm free on [suggest 2-3 specific times].

Looking forward to connecting further.

Best regards,
[Your Name]
[Your Contact Information]`}
                  </pre>
                </div>
              </div>
            </div>

            {/* Section 5 */}
            <div id="tracking" className="mb-16">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
                  <span className="text-2xl font-bold">✓</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-800">Tracking and Nurturing Professional Relationships</h2>
              </div>
              <div className="prose prose-lg max-w-none">
                <p>
                  Building a valuable network requires ongoing attention and nurturing. Systematic relationship management ensures you maintain and strengthen your professional connections over time.
                </p>
                <h3>Relationship Management Strategies</h3>
                <ul>
                  <li><strong>Create a Contact System:</strong> Use a CRM tool or spreadsheet to track key details about your connections.</li>
                  <li><strong>Schedule Regular Check-ins:</strong> Set reminders to reach out to valuable contacts every 2-3 months.</li>
                  <li><strong>Share Relevant Updates:</strong> Forward articles, opportunities, or information that might interest your contacts.</li>
                  <li><strong>Celebrate Achievements:</strong> Acknowledge promotions, company milestones, and personal accomplishments.</li>
                </ul>
                <div className="bg-green-50 p-6 rounded-lg border border-green-100 my-6">
                  <h4 className="text-green-700 font-semibold mb-3">Network Nurturing Calendar</h4>
                  <p>Implement this simple schedule to maintain relationships without overwhelming yourself:</p>
                  <ul>
                    <li><strong>Weekly:</strong> Engage with 3-5 connections on social media (comment, share posts)</li>
                    <li><strong>Monthly:</strong> Send personalized check-in messages to 5-10 key contacts</li>
                    <li><strong>Quarterly:</strong> Schedule in-person or virtual meetings with your most valuable connections</li>
                    <li><strong>Annually:</strong> Review and update your entire contact database</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download-guide" className="py-16 bg-gradient-to-br from-teal-500 to-blue-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Download The Complete Business Networking Guide</h2>
            <p className="text-xl mb-8 opacity-90">Get the full guide with templates, scripts, and worksheets to supercharge your networking efforts.</p>
            <form className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <input 
                  type="email" 
                  placeholder="Your email address" 
                  className="flex-1 px-4 py-3 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  required
                />
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-white text-blue-600 font-medium rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
                >
                  Get Free Guide
                </button>
              </div>
              <p className="text-sm mt-3 opacity-80">We respect your privacy. Unsubscribe at any time.</p>
            </form>
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 text-gray-800">Related Resources You Might Like</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link to="/linkedin-guide" className="block group">
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-6 transition-all hover:shadow-md">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center mb-4">
                    <FaLinkedin className="text-xl" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-blue-600 transition-colors">LinkedIn Networking Guide</h3>
                  <p className="text-gray-600">Learn how to build your personal brand and grow your professional network on LinkedIn.</p>
                </div>
              </Link>
              <div className="bg-gray-50 border border-gray-100 rounded-lg p-6 opacity-70">
                <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2">Personal Branding 101</h3>
                <p className="text-gray-600">Discover how to create and maintain a compelling personal brand. <span className="text-yellow-600 font-medium">Coming Soon</span></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section with Schema */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 text-gray-800">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">How often should I attend networking events?</h3>
                <p className="text-gray-600">The ideal frequency depends on your goals and capacity. Quality matters more than quantity—aim for 1-2 meaningful events per month rather than attending many events superficially. Focus on events that align with your industry and professional goals.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">What if I'm introverted or uncomfortable with networking?</h3>
                <p className="text-gray-600">Networking can be challenging for introverts, but it's still valuable. Try setting small, achievable goals (like having three quality conversations), bringing a colleague for support, or volunteering at events to give yourself a structured role. Online networking can also be a comfortable starting point.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">How do I politely end a conversation at a networking event?</h3>
                <p className="text-gray-600">Exit conversations gracefully by saying, "It's been great talking with you about [specific topic]. I should circulate a bit more, but I'd love to continue our conversation. Can we exchange contact information?" This acknowledges the value of your interaction while creating a natural endpoint.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusinessNetworkingBasics;
